import { useEffect, useState } from 'react';
import { Button, Input, Select, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, Modal, EmptyState } from '../../../../shared/components';
import { PlusIcon, EditIcon, TrashIcon } from '../../../../shared/icons';
import { getResultsRepository } from '../../../../shared/services/results/repositoryFactory';
import { AccountCatalogItem, CreateAccountInput, TipoCuenta } from '../../../../shared/services/results/types';

const repo = getResultsRepository();
const TIPO_CUENTA_OPTIONS = [{ value: 'INGRESO', label: 'INGRESO' }, { value: 'GASTOS', label: 'GASTOS' }];
const emptyForm: CreateAccountInput = { cuentaCodigo: '', cuentaDescripcion: '', tipoCuenta: 'INGRESO', isActive: true };

/** Parámetros > Catálogo de Cuentas: PUC interno usado para nombrar cuentas de forma consistente en ambas hojas. */
export function AccountCatalogTab() {
  const toast = useToast();
  const [rows, setRows] = useState<AccountCatalogItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AccountCatalogItem | null>(null);
  const [form, setForm] = useState<CreateAccountInput>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState<AccountCatalogItem | null>(null);

  const load = () => {
    setIsLoading(true);
    repo.listAccounts().then((r) => {
      setRows(r);
      setIsLoading(false);
    });
  };
  useEffect(load, []);

  const openCreate = () => {
    setForm(emptyForm);
    setCreating(true);
  };
  const openEdit = (r: AccountCatalogItem) => {
    setForm({ cuentaCodigo: r.cuentaCodigo, cuentaDescripcion: r.cuentaDescripcion, tipoCuenta: r.tipoCuenta, isActive: r.isActive });
    setEditing(r);
  };
  const close = () => {
    setCreating(false);
    setEditing(null);
  };
  const save = async () => {
    if (!form.cuentaCodigo || !form.cuentaDescripcion) {
      toast.show('Código y descripción son obligatorios.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      if (editing) {
        await repo.updateAccount(editing.id, form);
        toast.show('Cuenta actualizada.');
      } else {
        await repo.createAccount(form);
        toast.show('Cuenta agregada.');
      }
      close();
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const columns: Column<AccountCatalogItem>[] = [
    { key: 'codigo', header: 'Código', render: (r) => r.cuentaCodigo, sortable: true, sortValue: (r) => r.cuentaCodigo },
    { key: 'descripcion', header: 'Descripción', render: (r) => r.cuentaDescripcion },
    { key: 'tipo', header: 'Tipo', render: (r) => <Badge tone={r.tipoCuenta === 'INGRESO' ? 'info' : 'warning'}>{r.tipoCuenta}</Badge> },
    { key: 'activo', header: 'Estado', render: (r) => <Badge tone={r.isActive ? 'success' : 'neutral'}>{r.isActive ? 'Activo' : 'Inactivo'}</Badge> },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="flex gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar" onClick={() => openEdit(r)} className="h-9 w-9 text-ink-500 hover:bg-ink-100" />
          <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => setDeleting(r)} className="h-9 w-9 text-critical hover:bg-red-50" />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Button leftIcon={<PlusIcon width={18} height={18} />} onClick={openCreate}>
        Agregar cuenta
      </Button>

      {rows.length === 0 && !isLoading ? (
        <EmptyState icon="🧾" title="Sin cuentas" description="Este catálogo es solo informativo - el import acepta cualquier cuenta que venga en el Excel." />
      ) : (
        <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} isLoading={isLoading} pageSize={10} pageSizeOptions={[10, 25, 50]} emptyTitle="Sin cuentas" />
      )}

      <Modal open={creating || !!editing} onClose={close} title={editing ? 'Editar cuenta' : 'Agregar cuenta'}>
        <div className="space-y-3">
          <Input label="Código" value={form.cuentaCodigo} onChange={(e) => setForm((f) => ({ ...f, cuentaCodigo: e.target.value }))} />
          <Input label="Descripción" value={form.cuentaDescripcion} onChange={(e) => setForm((f) => ({ ...f, cuentaDescripcion: e.target.value }))} />
          <Select label="Tipo Cuenta" options={TIPO_CUENTA_OPTIONS} value={form.tipoCuenta} onChange={(e) => setForm((f) => ({ ...f, tipoCuenta: e.target.value as TipoCuenta }))} />
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={close}>Cancelar</Button>
            <Button fullWidth onClick={save} isLoading={isSaving}>Guardar</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title="Eliminar cuenta"
        description={`¿Eliminar la cuenta "${deleting?.cuentaCodigo}"?`}
        tone="danger"
        confirmLabel="Eliminar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await repo.deleteAccount(deleting.id);
          setDeleting(null);
          load();
        }}
      />
    </div>
  );
}
